import React, { useEffect, useMemo, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import {
  apexBarChartOptions,
  groupByForPaymentMethods,
  paymentMethodTypes,
} from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import ReactApexChart from "react-apexcharts"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import {
  getAllSalesReports,
  getPaymentMethodReports,
} from "store/actions/reportsAction"
import { Dropdown, DropdownButton, Form } from "react-bootstrap"
import { getAllCityList } from "store/actions/cityAction"
import { getAllRegionList } from "store/actions/regionAction"
import { getCountryList } from "store/actions/countriesAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import { getBranchCountryList } from "store/actions/commonAction"
import { getPaymentMethodsScope } from "store/actions/globalSettingAction"
import { filterPaymentMethodTypesByTenantScope } from "pages/AppSettings/paymentMethodConfig"

const formatDateTime = date => {
  const pad = value => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatDateTime(startOfDay),
    max: formatDateTime(endOfDay),
  }
}

const PaymentsReports = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Payment Methods Reports ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [showGraph, setShowGraph] = useState(false)
  const [isShowGraphBtn, setIsShowGraphBtn] = useState(true)
  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    paymentsMethodReports,
  } = useSelector(state => state.Reports)

  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      16,
      "view_payments_reports",
      accountType,
      accountScope
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
    })
  }, [userRole, accountType, accountScope, navigate])

  const { branchsList, countryList } = useSelector(state => state.Common)
  const { paymentMethodsScope } = useSelector(state => state.GlobalSettings)

  const [selectedFilters, setSelectedFilters] = useState({
    branchId: [],
    groupBy: "",
    paymentMethod: [],
    countryId: [],
    regionId: [],
    cityId: [],
  })
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange())
  const flatpickrRef = useRef(null)
  const dropdownRef = useRef(null)
  const { regionList } = useSelector(state => state.Region)
  const { cityList } = useSelector(state => state.City)

  const [options, setOptions] = useState(apexBarChartOptions)
  const [series, setSeries] = useState()
  const [graphMetricType, setGraphMetricType] = useState("totalAmount")

  const canFilterByBranch = isPermissionEnabled(
    userRole,
    5,
    "branchLocations_1",
    accountType,
    accountScope
  )
  const canFilterByRegion = isPermissionEnabled(
    userRole,
    25,
    "region_1",
    accountType,
    accountScope
  )
  const canFilterByCity = isPermissionEnabled(
    userRole,
    26,
    "city_1",
    accountType,
    accountScope
  )

  const allowedPaymentMethodTypes = useMemo(
    () =>
      filterPaymentMethodTypesByTenantScope(
        paymentMethodTypes,
        paymentMethodsScope?.cards || []
      ),
    [paymentMethodsScope]
  )

  const paymentMethodFilterOptions = useMemo(
    () =>
      allowedPaymentMethodTypes.map(item => ({
        label: t(item.label),
        value: item.value,
      })),
    [allowedPaymentMethodTypes, language, t]
  )

  useEffect(() => {
    if (paymentsMethodReports) {
      const updatedCategories = paymentsMethodReports?.map(item =>
        t(item.paymentMethod)
      )
      const updatedSeries = paymentsMethodReports?.map(
        item => item[graphMetricType]
      )
      const dataSeries = [
        {
          data: updatedSeries,
        },
      ]

      setSeries(dataSeries)

      setOptions(prev => ({
        ...prev,
        xaxis: {
          categories: updatedCategories,
        },
      }))
    }
  }, [paymentsMethodReports, graphMetricType, language])

  useEffect(() => {
    fetchPaymentsMethodReports()
    dispatch(getPaymentMethodsScope())
  }, [])

  useEffect(() => {
    if (canFilterByBranch) {
      fetchBranchCountryList()
    }
  }, [canFilterByBranch])

  // Drop previously selected methods that are no longer allowed for this tenant.
  useEffect(() => {
    const allowedValues = new Set(
      allowedPaymentMethodTypes.map(item => item.value)
    )
    setSelectedFilters(prev => {
      const current = prev.paymentMethod || []
      if (!Array.isArray(current) || current.length === 0) return prev
      const next = current.filter(item => allowedValues.has(item.value))
      if (next.length === current.length) return prev
      return { ...prev, paymentMethod: next }
    })
  }, [allowedPaymentMethodTypes])

  const fetchBranchCountryList = () => {
    if (!canFilterByBranch) return
    dispatch(getBranchCountryList())
  }

  const fetchRegionList = countryId => {
    if (!canFilterByRegion) return
    dispatch(getAllRegionList(countryId))
  }

  const fetchCityList = (countryId, regionId) => {
    if (!canFilterByCity) return
    const payload = {
      countryId,
      regionId,
    }
    dispatch(getAllCityList(payload))
  }

  const fetchPaymentsMethodReports = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      branchId: [],
      groupBy: "",
      dateRange: dateRange.min && dateRange.max ? dateRange : {},
    }
    dispatch(getPaymentMethodReports(payload))
    setIsApiCalled(true)
  }

  const filters = [
    ...(canFilterByBranch
      ? [
          {
            name: "branchId",
            type: "multiSelect",
            options: [
              ...branchsList.map(branch => ({
                label: language === "sa" ? branch.nameAr : branch.name,
                value: branch._id,
              })),
            ],
            placeholder: t("Select Branch"),
          },
        ]
      : []),
    {
      name: "groupBy",
      type: "singleSelect",
      options: [
        ...groupByForPaymentMethods.map(item => ({
          label: t(item.label),
          value: item.value,
        })),
      ],
      placeholder: t("Select Group By"),
    },
    {
      name: "paymentMethod",
      type: "multiSelect",
      options: paymentMethodFilterOptions,
      placeholder: t("Payment Methods"),
    },
    ...(canFilterByBranch
      ? [
          {
            name: "countryId",
            type: "singleSelect",
            options: [
              ...countryList.map(item => ({
                label: language === "sa" ? item.nameAr : item.name,
                value: item._id,
              })),
            ],
            placeholder: t("Select Country"),
          },
        ]
      : []),
  ]
  // ADD REGION DROPDOWN
  if (
    canFilterByRegion &&
    Object.keys(selectedFilters.countryId).length > 0
  ) {
    filters.push({
      name: "regionId",
      type: "singleSelect",
      options: regionList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select Region"),
    })
  }

  // ADD CITY DROPDOWN
  if (canFilterByCity && Object.keys(selectedFilters.regionId).length > 0) {
    filters.push({
      name: "cityId",
      type: "singleSelect",
      options: cityList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select City"),
    })
  }

  const handleSearch = () => {
    const { branchId, groupBy, paymentMethod, countryId, regionId, cityId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const paymentMethodIds = paymentMethod?.map(item => item.value)
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    //HIDE SHOW GRAPH BUTTON IF HAVE GROUP BY FILTER
    if (groupBy === "" || groupBy.value === "") {
      setIsShowGraphBtn(true)
    } else {
      setIsShowGraphBtn(false)
      setShowGraph(false)
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      branchId: branchIds,
      groupBy: groupByValue,
      paymentMethod: paymentMethodIds,
      dateRange: {
        min: dateRange.min,
        max: dateRange.max,
      },
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
    }
    dispatch(getPaymentMethodReports(payload))
    navigate(`/payments-reports?page=${1}`)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleFilterChange = (selectedValue, filterName) => {
    const value = selectedValue.value

    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
    if (filterName === "countryId") {
      // GET REGION LIST
      fetchRegionList(value)
      setSelectedFilters(prev => ({
        ...prev,
        regionId: [],
        cityId: [],
      }))
    } else if (filterName === "regionId") {
      // GET CITIES LIST
      const { countryId } = selectedFilters
      fetchCityList(countryId.value, value)
      setSelectedFilters(prev => ({
        ...prev,
        cityId: [],
      }))
    }
  }

  const onPageChange = page => {
    const { branchId, paymentMethod, groupBy, countryId, regionId, cityId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const paymentMethodIds = paymentMethod?.map(item => item.value)

    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      branchId: branchIds,
      paymentMethod: paymentMethodIds,
      groupBy: groupByValue,
      dateRange: {
        min: dateRange.min,
        max: dateRange.max,
      },
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
    }
    dispatch(getPaymentMethodReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/payments-reports?page=${page}`)
        }
      })
      .catch(error => {})
  }

  const handleReset = () => {
    const defaultDateRange = getDefaultDateRange()
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      branchId: [],
      groupBy: "",
      dateRange: defaultDateRange,
      countryId: [],
      regionId: [],
      cityId: [],
    }
    dispatch(getPaymentMethodReports(payload))
    navigate(`/payments-reports?page=${1}`)
    setSelectedFilters(prev => ({
      branchId: [],
      paymentMethod: [],
      groupBy: "",
      countryId: [],
      regionId: [],
      cityId: [],
    }))
    setDateRange(defaultDateRange)
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate(
        [defaultDateRange.min, defaultDateRange.max],
        true
      )
    }
  }

  const exportToCSV = async () => {
    const { branchId, paymentMethod, groupBy, countryId, regionId, cityId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const paymentMethodIds = paymentMethod?.map(item => item.value)
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "REPORT-PAYMENTMETHOD",
      exportType: "csv",
      branchId: branchIds,
      keyword: "", // STATIC
      paymentMethod: paymentMethodIds,
      dateRange: hasDateRange
        ? {
            min: dateRange.min,
            max: dateRange.max,
          }
        : {},
      groupBy: groupByValue,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response

      const fileName = hasDateRange
        ? t(`payment_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Payment Report Till Date")
      DownloadCSV(csvData, fileName)
    }
  }

  const handleExcelDownload = async () => {
    const { branchId, paymentMethod, groupBy, countryId, regionId, cityId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const paymentMethodIds = paymentMethod?.map(item => item.value)
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const hasDateRange = dateRange?.min && dateRange?.max

    const payload = {
      type: "REPORT-PAYMENTMETHOD",
      exportType: "xlsx",
      branchId: branchIds,
      keyword: "", // STATIC
      paymentMethod: paymentMethodIds,
      dateRange: hasDateRange
        ? {
            min: dateRange.min,
            max: dateRange.max,
          }
        : {},
      groupBy: groupByValue,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      const fileName = hasDateRange
        ? t(`payment_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Payment Report Till Date")
      DownloadXLSX(xlsxData, fileName)
    }
  }

  const calculateTotal = property => {
    if (!paymentsMethodReports || !Array.isArray(paymentsMethodReports)) {
      return 0
    }
    return paymentsMethodReports.reduce((total, currentItem) => {
      const value = currentItem[property] || 0
      return total + value
    }, 0)
  }

  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState([
    "totalAmount",
    "totalCount",
    "activeOrdersAmount",
    "activeOrdersCount",
    "completedOrdersAmount",
    "completedOrdersCount",
    "canceledOrdersAmount",
    "canceledOrdersCount",
  ])

  // const getColumns = () => {
  //   const { groupBy } = selectedFilters
  //   const baseColumns = [
  //     { name: "Select All", value: "selectAll" },
  //     { name: "Total Amount", value: "totalAmount" },
  //     { name: "Total Count", value: "totalCount" },
  //     { name: "Active Orders Amount", value: "activeOrdersAmount" },
  //     { name: "Active Orders Count", value: "activeOrdersCount" },
  //     { name: "Completed Orders Amount", value: "completedOrdersAmount" },
  //     { name: "Completed Orders Count", value: "completedOrdersCount" },
  //     { name: "Canceled Orders Amount", value: "canceledOrdersAmount" },
  //     { name: "Canceled Orders Count", value: "canceledOrdersCount" },
  //   ]
  //   if (groupBy.value === "branch") {
  //     return [...baseColumns, { name: "Branch", value: "branch" }]
  //   } else if (groupBy.value === "orderType") {
  //     return [...baseColumns, { name: "Order Type", value: "orderType" }]
  //   } else if (groupBy.value === "date") {
  //     return [...baseColumns, { name: "Date", value: "date" }]
  //   } else if (
  //     [
  //       "year",
  //       "quarter",
  //       "month",
  //       "dayOfMonth",
  //       "week",
  //       "dayOfYear",
  //       "dayOfWeek",
  //       "half",
  //     ].includes(groupBy.value)
  //   ) {
  //     return [...baseColumns, { name: "Year", value: "year" }]
  //   }
  //   return baseColumns
  // }
  // const columns = getColumns()

  const columns = [
    { name: t("Select All"), value: "selectAll" },
    { name: t("Total Amount"), value: "totalAmount" },
    { name: t("Total Count"), value: "totalCount" },
    { name: t("Active Orders Amount"), value: "activeOrdersAmount" },
    { name: t("Active Orders Count"), value: "activeOrdersCount" },
    { name: t("Completed Orders Amount"), value: "completedOrdersAmount" },
    { name: t("Completed Orders Count"), value: "completedOrdersCount" },
    { name: t("Canceled Orders Amount"), value: "canceledOrdersAmount" },
    { name: t("Canceled Orders Count"), value: "canceledOrdersCount" },
  ]

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown)
  }

  const handleColumnSelect = (e, column) => {
    e.stopPropagation() // Prevent event bubbling
    if (column === "selectAll") {
      const allColumns = columns.map(col => col.value)
      if (selectedColumns.length === allColumns.length - 1) {
        setSelectedColumns([])
      } else {
        setSelectedColumns(allColumns.filter(col => col !== "selectAll"))
      }
    } else {
      if (selectedColumns.includes(column)) {
        setSelectedColumns(selectedColumns.filter(c => c !== column))
      } else {
        setSelectedColumns([...selectedColumns, column])
      }
    }
  }

  const handleClickOutside = event => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            {showGraph && (
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between">
                    <CardTitle>{t("Payment Report Graph")}</CardTitle>
                    <div>
                      <select
                        name=""
                        id=""
                        value={graphMetricType}
                        onChange={e => {
                          setGraphMetricType(e.target.value)
                        }}
                        style={{
                          background: "transparent",
                          border: "1px solid #495057",
                          borderRadius: 5,
                          padding: 5,
                        }}
                      >
                        <option value="totalAmount">{t("Amount")}</option>
                        <option value="totalCount">{t("Count")}</option>
                        <option value="activeOrdersAmount">
                          {t("Active Order Amount")}
                        </option>
                        <option value="activeOrdersCount">
                          {t("Active Order Count")}
                        </option>
                        <option value="completedOrdersAmount">
                          {t("Completed Orders Amount")}
                        </option>
                        <option value="completedOrdersCount">
                          {t("Completed Orders Count")}
                        </option>
                        <option value="canceledOrdersAmount">
                          {t("Canceled Orders Amount")}
                        </option>
                        <option value="canceledOrdersCount">
                          {t("Canceled Orders Count")}
                        </option>
                      </select>
                    </div>
                  </div>
                  {loading ? null : (
                    <div style={{ direction: "ltr" }}>
                      <ReactApexChart
                        options={options}
                        series={series}
                        type="bar"
                        height="350"
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
            <Card>
              <DataExportDropdown
                cardTitle={t("Payment Reports")}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isAddBtn={loading ? false : isShowGraphBtn}
                addBtnText={`${showGraph ? t("Hide") : t("Show")} ${t(
                  "Graph"
                )}`}
                onAddBtnClick={() => setShowGraph(!showGraph)}
                isExportData={loading ? false : true}
                isCustomColumn={loading ? false : true}
                showDropdown={showDropdown}
                columns={columns}
                dropdownRef={dropdownRef}
                selectedColumns={selectedColumns}
                handleColumnSelect={handleColumnSelect}
                handleDropdownToggle={handleDropdownToggle}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  hideSearch={true}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  setDateRange={setDateRange}
                  colSize={4}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : paymentsMethodReports.length === 0 ? (
                    <p className="text-center my-3">
                      {t("No payments method found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr className="full_th">
                          <th>{t("Payment Method")}</th>
                          {selectedFilters?.groupBy?.value === "branch" && (
                            <th>{t("Name")}</th>
                          )}
                          {selectedFilters?.groupBy?.value === "orderType" && (
                            <th>{t("Order Type")}</th>
                          )}
                          {selectedFilters?.groupBy?.value === "date" && (
                            <th>{t("Date")}</th>
                          )}
                          {selectedFilters?.groupBy?.value === "hour" && (
                            <th>{t("Hour")}</th>
                          )}

                          {[
                            "year",
                            "quarter",
                            "month",
                            "dayOfMonth",
                            "week",
                            "dayOfYear",
                            "dayOfWeek",
                            "half",
                          ].includes(selectedFilters?.groupBy?.value) && (
                            <th>{t("Year")}</th>
                          )}

                          {["month", "dayOfMonth"].includes(
                            selectedFilters?.groupBy?.value
                          ) && <th>{t("Month")}</th>}

                          {selectedFilters?.groupBy?.value === "quarter" && (
                            <th>{t("Quarter")}</th>
                          )}
                          {selectedFilters?.groupBy?.value === "half" && (
                            <th>{t("Half")}</th>
                          )}
                          {selectedFilters?.groupBy?.value === "dayOfMonth" && (
                            <th>{t("Day")}</th>
                          )}
                          {selectedFilters?.groupBy?.value === "week" && (
                            <th>{t("Week")}</th>
                          )}
                          {selectedFilters?.groupBy?.value === "dayOfYear" && (
                            <th>{t("Day Of Year")}</th>
                          )}
                          {selectedFilters?.groupBy?.value === "dayOfWeek" && (
                            <th>{t("Day Of Week")}</th>
                          )}
                          {/* <th>Total Amount</th>
                          <th>Total Count</th>
                          <th>Active Orders Amount</th>
                          <th>Active Orders Count</th>
                          <th>Completed Orders Amount</th>
                          <th>Completed Orders Count</th>
                          <th>Canceled Orders Amount</th>
                          <th>Canceled Orders Count</th> */}
                          {columns
                            .filter(
                              column =>
                                column.value !== "selectAll" &&
                                selectedColumns.includes(column.value)
                            )
                            .map(column => (
                              <th key={column.value}>{column.name}</th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paymentsMethodReports?.map(
                          (
                            {
                              paymentMethod,
                              name,
                              hour,
                              date,
                              week,
                              month,
                              year,
                              day,
                              quarter,
                              half,
                              dayOfYear,
                              orderType,
                              dayOfWeek,
                              totalCount,
                              totalAmount,
                              activeOrdersCount,
                              activeOrdersAmount,
                              completedOrdersCount,
                              completedOrdersAmount,
                              canceledOrdersCount,
                              canceledOrdersAmount,
                              countryCode,
                            },
                            index
                          ) => (
                            <tr key={index} className="capitalize_td">
                              <td>{t(paymentMethod)}</td>
                              {selectedFilters?.groupBy?.value === "branch" && (
                                <td>{name}</td>
                              )}
                              {selectedFilters?.groupBy?.value ===
                                "orderType" && <td>{t(orderType)}</td>}
                              {selectedFilters?.groupBy?.value === "date" && (
                                <td>{date}</td>
                              )}
                              {selectedFilters?.groupBy?.value === "hour" && (
                                <td>{hour}</td>
                              )}

                              {[
                                "year",
                                "quarter",
                                "month",
                                "dayOfMonth",
                                "week",
                                "dayOfYear",
                                "dayOfWeek",
                                "half",
                              ].includes(selectedFilters?.groupBy?.value) && (
                                <td>{year}</td>
                              )}

                              {["month", "dayOfMonth"].includes(
                                selectedFilters?.groupBy?.value
                              ) && <td>{month}</td>}

                              {selectedFilters?.groupBy?.value ===
                                "quarter" && <td>{quarter}</td>}

                              {selectedFilters?.groupBy?.value === "half" && (
                                <td>{half}</td>
                              )}

                              {selectedFilters?.groupBy?.value ===
                                "dayOfMonth" && <td>{day}</td>}

                              {selectedFilters?.groupBy?.value === "week" && (
                                <td>{week}</td>
                              )}

                              {selectedFilters?.groupBy?.value ===
                                "dayOfYear" && <td>{dayOfYear}</td>}

                              {selectedFilters?.groupBy?.value ===
                                "dayOfWeek" && <td>{dayOfWeek}</td>}

                              {selectedColumns.includes("totalAmount") && (
                                <td>
                                  {totalAmount}{" "}
                                  {t(
                                    countryList.find(
                                      c => c.countryCode === countryCode
                                    )?.currency || ""
                                  )}
                                </td>
                              )}
                              {selectedColumns.includes("totalCount") && (
                                <td>{totalCount}</td>
                              )}

                              {selectedColumns.includes(
                                "activeOrdersAmount"
                              ) && (
                                <td>
                                  {activeOrdersAmount}{" "}
                                  {t(
                                    countryList.find(
                                      c => c.countryCode === countryCode
                                    )?.currency
                                  ) || ""}
                                </td>
                              )}

                              {selectedColumns.includes(
                                "activeOrdersCount"
                              ) && <td>{activeOrdersCount}</td>}

                              {selectedColumns.includes(
                                "completedOrdersAmount"
                              ) && (
                                <td>
                                  {completedOrdersAmount}{" "}
                                  {t(
                                    countryList.find(
                                      c => c.countryCode === countryCode
                                    )?.currency || ""
                                  )}
                                </td>
                              )}
                              {selectedColumns.includes(
                                "completedOrdersCount"
                              ) && <td>{completedOrdersCount}</td>}

                              {selectedColumns.includes(
                                "canceledOrdersAmount"
                              ) && (
                                <td>
                                  {canceledOrdersAmount}{" "}
                                  {t(
                                    countryList.find(
                                      c => c.countryCode === countryCode
                                    )?.currency || ""
                                  )}
                                </td>
                              )}

                              {selectedColumns.includes(
                                "canceledOrdersCount"
                              ) && <td>{canceledOrdersCount}</td>}
                            </tr>
                          )
                        )}
                      </tbody>
                      {/* <tfoot>
                        <tr>
                          <td>Totals</td>
                          {[
                            "branch",
                            "date",
                            "orderType",
                            "hour",
                            "half",
                            "quarter",
                            "dayOfMonth",
                            "month",
                            "week",
                            "dayOfYear",
                            "dayOfWeek",
                          ].includes(selectedFilters?.groupBy?.value) && (
                            <td></td>
                          )}

                          {[
                            "year",
                            "quarter",
                            "month",
                            "dayOfMonth",
                            "week",
                            "dayOfYear",
                            "dayOfWeek",
                            "half",
                          ].includes(selectedFilters?.groupBy?.value) && (
                            <td></td>
                          )}
                          {["dayOfMonth"].includes(
                            selectedFilters?.groupBy?.value
                          ) && <td></td>}

                          {selectedColumns.includes("totalAmount") && (
                            <td>{calculateTotal("totalAmount")} SAR</td>
                          )}
                          {selectedColumns.includes("totalCount") && (
                            <td>{calculateTotal("totalCount")} </td>
                          )}
                          {selectedColumns.includes("activeOrdersAmount") && (
                            <td>{calculateTotal("activeOrdersAmount")} SAR</td>
                          )}
                          {selectedColumns.includes("activeOrdersCount") && (
                            <td>{calculateTotal("activeOrdersCount")} </td>
                          )}
                          {selectedColumns.includes(
                            "completedOrdersAmount"
                          ) && (
                            <td>
                              {calculateTotal("completedOrdersAmount")} SAR
                            </td>
                          )}
                          {selectedColumns.includes("completedOrdersCount") && (
                            <td>{calculateTotal("completedOrdersCount")} </td>
                          )}
                          {selectedColumns.includes("canceledOrdersAmount") && (
                            <td>
                              {calculateTotal("canceledOrdersAmount")} SAR
                            </td>
                          )}
                          {selectedColumns.includes("canceledOrdersCount") && (
                            <td>{calculateTotal("canceledOrdersCount")} </td>
                          )}
                        </tr>
                      </tfoot> */}
                    </Table>
                  )}
                </div>

                {!loading && paymentsMethodReports.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={paymentsMethodReports}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default PaymentsReports
